import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  BookOpen, Target, Trophy, Lock, Menu, X,
  Star, Award, CreditCard
} from 'lucide-react';
import flashcardsData from '../data/flashcards';
import quizzesData from '../data/quizzes';
import examsData from '../data/exams';
import careerPathsData from '../data/careerPaths';
import { isStripeConfigured, redirectToCheckout } from '../services/stripe';
import { getUserSubscription, logUserSignup, hasSignupBeenLogged } from '../lib/supabase';
/**
 * Free Trial App Component
 * Provides limited access: 1 exam, 15 flashcards, 2 quizzes
 * Features left-sidebar navigation
 */
const FreeTrialApp = () => {
  const { user, logout } = useAuth0();

  // Left sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  // Free trial usage tracking
  const [trialUsage, setTrialUsage] = useState(() => {
    const saved = localStorage.getItem(`trial_${user?.email}`);
    return saved ? JSON.parse(saved) : {
      flashcardsUsed: 0,
      quizzesCompleted: 0,
      examsCompleted: 0,
      startDate: new Date().toISOString()
    };
  });

  // Free trial limits
  const TRIAL_LIMITS = {
    flashcards: 15,
    quizzes: 2,
    exams: 1
  };

    // Free trial item IDs - which specific items are accessible in free trial
    const FREE_TRIAL_QUIZ_IDS = [1, 2]; // Quiz IDs 1 and 2 are free
    const FREE_TRIAL_EXAM_IDS = [1];    // Only exam ID 1 is free

    // Access check functions for quizzes and exams
    const canAccessQuiz = (quizId) => isPremium || FREE_TRIAL_QUIZ_IDS.includes(quizId);
    const canAccessExam = (examId) => isPremium || FREE_TRIAL_EXAM_IDS.includes(examId);

  // Exam history storage
  const [examHistory, setExamHistory] = useState(() => {
    const saved = localStorage.getItem(`exam_history_${user?.email}`);
    return saved ? JSON.parse(saved) : {};
  });

  // Quiz history storage
  const [quizHistory, setQuizHistory] = useState(() => {
    const saved = localStorage.getItem(`quiz_history_${user?.email}`);
    return saved ? JSON.parse(saved) : {};
  });

  // All state declarations
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizCompleteConfirm, setShowQuizCompleteConfirm] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [currentExamQuestion, setCurrentExamQuestion] = useState(0);
  const [currentExamSection, setCurrentExamSection] = useState(0);
  const [examAnswers, setExamAnswers] = useState({});
  const [showExamCompleteConfirm, setShowExamCompleteConfirm] = useState(false);
  const [selectedCareerPath, setSelectedCareerPath] = useState(null);
  const [selectedFlashcardCategory, setSelectedFlashcardCategory] = useState('All');
  const [selectedQuizCategory, setSelectedQuizCategory] = useState('All');
  const [quizReviewData, setQuizReviewData] = useState(null);
  const [examReviewData, setExamReviewData] = useState(null);
  const [quizMode, setQuizMode] = useState(null); // 'exam' or 'study'
  const [examMode, setExamMode] = useState(null); // 'exam' or 'study'
  const [showModeSelection, setShowModeSelection] = useState(null); // stores quiz/exam object while selecting mode
  const [showExplanation, setShowExplanation] = useState(false); // for study mode
  const [showHistoryView, setShowHistoryView] = useState(null); // 'quiz' or 'exam' to show history

  // ASVAB Subject Categories
  const ASVAB_CATEGORIES = [
    'All',
    'General Science',
    'Arithmetic Reasoning',
    'Word Knowledge',
    'Paragraph Comprehension',
    'Mathematics Knowledge',
    'Electronics Information',
    'Auto & Shop Information',
    'Mechanical Comprehension'
  ];

  // Save usage to localStorage
  useEffect(() => {
    localStorage.setItem(`trial_${user?.email}`, JSON.stringify(trialUsage));
  }, [trialUsage, user?.email]);

  // Save exam history to localStorage
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(`exam_history_${user.email}`, JSON.stringify(examHistory));
    }
  }, [examHistory, user?.email]);

  // Save quiz history to localStorage
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(`quiz_history_${user.email}`, JSON.stringify(quizHistory));
    }
  }, [quizHistory, user?.email]);

  // Check for active subscription
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setSubscriptionLoading(false);
        return;
      }

      try {
        console.log('Checking subscription for user:', user.sub, user.email);
        const sub = await getUserSubscription(user.sub, user.email);

        if (sub) {
          console.log('Found subscription:', sub);
          setSubscription(sub);

          // Check if subscription is still valid
          const now = new Date();
          const endDate = new Date(sub.current_period_end);
          const isActive = sub.status === 'active' && endDate > now;

          setIsPremium(isActive);
          console.log('Premium status:', isActive);
        } else {
          console.log('No active subscription found');
          setIsPremium(false);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        setIsPremium(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, [user]);

  // Log user signup for billing tracking
  useEffect(() => {
    const logSignup = async () => {
      // Wait for user and subscription to load
      if (!user || subscriptionLoading) return;

      try {
        // Check if signup has already been logged
        const alreadyLogged = await hasSignupBeenLogged(user.sub);
        if (alreadyLogged) {
          console.log('Signup already logged for user:', user.sub);
          return;
        }

        // Get checkout data from localStorage (if available)
        const checkoutEmail = localStorage.getItem('checkout_email');
        const sessionId = localStorage.getItem('stripe_session_id');

        // Extract auth method from Auth0 user.sub (e.g., "google-oauth2|123" or "auth0|123")
        const authMethod = user.sub.split('|')[0];

        console.log('Logging signup for user:', user.sub, 'with method:', authMethod);

        // Log the signup
        await logUserSignup({
          auth0_id: user.sub,
          signup_email: user.email,
          auth_method: authMethod,
          checkout_email: checkoutEmail,
          stripe_session_id: sessionId,
          subscription_found: Boolean(subscription),
          notes: checkoutEmail ? 'Signup after payment' : 'Direct signup'
        });

        console.log('Signup logged successfully');

        // Clear localStorage after logging to prevent duplicate logs
        if (checkoutEmail) {
          localStorage.removeItem('checkout_email');
          localStorage.removeItem('stripe_session_id');
          localStorage.removeItem('checkout_timestamp');
        }
      } catch (error) {
        console.error('Error logging signup:', error);
      }
    };

    logSignup();
  }, [user, subscription, subscriptionLoading]);

  // Load career path from localStorage on mount
  useEffect(() => {
    if (user?.email) {
      const saved = localStorage.getItem(`career_${user.email}`);
      if (saved) {
        try {
          setSelectedCareerPath(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to load career path:', e);
        }
      }
    }
  }, [user?.email]);

  // Save career path to localStorage
  useEffect(() => {
    if (selectedCareerPath) {
      localStorage.setItem(`career_${user?.email}`, JSON.stringify(selectedCareerPath));
    }
  }, [selectedCareerPath, user?.email]);

  // Handle career path selection
  const selectCareerPath = (careerPath) => {
    setSelectedCareerPath(careerPath);
  };

  // Real ASVAB data from imported files
  // Map ALL flashcards to include category field (subject → category) for accurate counts
  const allFlashcards = flashcardsData.map(card => ({
    ...card,
    category: card.subject
  }));

  // Limit viewable flashcards to trial limit (15 cards) - unless user has premium
  const flashcards = isPremium ? allFlashcards : allFlashcards.slice(0, TRIAL_LIMITS.flashcards);

  // Use real quiz data
  const quizzes = quizzesData;

  // Use real exam data
  const exams = examsData;

  // Navigation tabs
  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Trophy },
    { id: 'exams', name: 'Practice Exams', icon: Award },
    { id: 'quizzes', name: 'Quizzes', icon: Target },
    { id: 'flashcards', name: 'Flashcards', icon: BookOpen },
    { id: 'membership', name: 'Membership', icon: CreditCard },
  ];

  // Debug: Log tabs on mount
  useEffect(() => {
    console.log('FreeTrialApp tabs:', tabs);
  }, []);

  // Flashcard navigation is now handled inline in the Next Card button

  // Handle quiz start - show mode selection
    const startQuiz = (quiz) => {
          // Check access before allowing quiz start
          if (!canAccessQuiz(quiz.id)) {
                  setActiveTab('membership');
                  return;
          }
    setShowModeSelection({ type: 'quiz', data: quiz });
  };

  // Actually start quiz after mode selection
  const startQuizWithMode = (quiz, mode) => {
    setSelectedQuiz(quiz);
    setQuizMode(mode);
    setCurrentQuizQuestion(0);
    setQuizAnswers({});
    setShowQuizCompleteConfirm(false);
    setShowModeSelection(null);
    setShowExplanation(false);
  };

  // Handle quiz completion
  const completeQuiz = () => {
    // Premium users bypass trial limits
    const canComplete = isPremium || (trialUsage.quizzesCompleted < TRIAL_LIMITS.quizzes);

    if (canComplete && selectedQuiz) {
      // Calculate score
      let correctCount = 0;
      selectedQuiz.questions.forEach(question => {
        const answerKey = `${selectedQuiz.id}-${question.id}`;
        const userAnswer = quizAnswers[answerKey];
        if (userAnswer === question.correctAnswer) {
          correctCount++;
        }
      });

      const quizResult = {
        quiz: selectedQuiz,
        answers: quizAnswers,
        score: correctCount,
        total: selectedQuiz.questions.length,
        percentage: Math.round((correctCount / selectedQuiz.questions.length) * 100),
        completedAt: new Date().toISOString(),
        mode: quizMode
      };

      // Store review data for immediate viewing
      setQuizReviewData(quizResult);

      // Save to quiz history (replaces previous attempt)
      setQuizHistory(prev => ({
        ...prev,
        [selectedQuiz.id]: quizResult
      }));

      // Only track usage for non-premium users on first completion
      if (!isPremium && !quizHistory[selectedQuiz.id]) {
        setTrialUsage(prev => ({ ...prev, quizzesCompleted: prev.quizzesCompleted + 1 }));
      }
      setShowQuizCompleteConfirm(false);
    }
  };

  // Handle quiz exit (back to quiz list)
  const exitQuiz = () => {
    setSelectedQuiz(null);
    setCurrentQuizQuestion(0);
    setQuizAnswers({});
    setShowQuizCompleteConfirm(false);
  };

  // Handle quiz answer selection
  const selectQuizAnswer = (questionId, answerIndex) => {
    // Create unique key combining quiz ID and question ID to avoid collisions
    const answerKey = `${selectedQuiz.id}-${questionId}`;
    // Allow changing answers before submitting
    setQuizAnswers(prev => ({ ...prev, [answerKey]: answerIndex }));

    // Exam mode: auto-advance to next question
    if (quizMode === 'exam') {
      setTimeout(() => {
        if (selectedQuiz && currentQuizQuestion < selectedQuiz.questions.length - 1) {
          setCurrentQuizQuestion(currentQuizQuestion + 1);
          setShowExplanation(false);
        } else {
          // Last question - show complete dialog
          setShowQuizCompleteConfirm(true);
        }
      }, 300); // Small delay for visual feedback
    } else if (quizMode === 'study') {
      // Study mode: show explanation
      setShowExplanation(true);
    }
  };

  // Navigate quiz questions
  const nextQuizQuestion = () => {
    if (selectedQuiz && currentQuizQuestion < selectedQuiz.questions.length - 1) {
      setCurrentQuizQuestion(currentQuizQuestion + 1);
      setShowExplanation(false);
    }
  };

  const prevQuizQuestion = () => {
    if (currentQuizQuestion > 0) {
      setCurrentQuizQuestion(currentQuizQuestion - 1);
    }
  };

  // Handle exam start - show mode selection
  const startExam = (exam) => {
        // Check access before allowing exam start
        if (!canAccessExam(exam.id)) {
                setActiveTab('membership');
                return;
        }
    setShowModeSelection({ type: 'exam', data: exam });
  };

  // Actually start exam after mode selection
  const startExamWithMode = (exam, mode) => {
    setSelectedExam(exam);
    setExamMode(mode);
    setCurrentExamSection(0);
    setCurrentExamQuestion(0);
    setExamAnswers({});
    setShowExamCompleteConfirm(false);
    setShowModeSelection(null);
    setShowExplanation(false);
  };

  // Handle exam completion
  const completeExam = () => {
    // Premium users bypass trial limits
    const canComplete = isPremium || (trialUsage.examsCompleted < TRIAL_LIMITS.exams);

    if (canComplete && selectedExam) {
      // Calculate score across all sections
      let correctCount = 0;
      let totalQuestions = 0;

      selectedExam.sections.forEach((section, sectionIndex) => {
        section.questions.forEach(question => {
          totalQuestions++;
          const answerKey = `${selectedExam.id}-${sectionIndex}-${question.id}`;
          const userAnswer = examAnswers[answerKey];
          if (userAnswer === question.correctAnswer) {
            correctCount++;
          }
        });
      });

      const examResult = {
        exam: selectedExam,
        answers: examAnswers,
        score: correctCount,
        total: totalQuestions,
        percentage: Math.round((correctCount / totalQuestions) * 100),
        completedAt: new Date().toISOString(),
        mode: examMode
      };

      // Store review data for immediate viewing
      setExamReviewData(examResult);

      // Save to exam history (replaces previous attempt)
      setExamHistory(prev => ({
        ...prev,
        [selectedExam.id]: examResult
      }));

      // Only track usage for non-premium users on first completion
      if (!isPremium && !examHistory[selectedExam.id]) {
        setTrialUsage(prev => ({ ...prev, examsCompleted: prev.examsCompleted + 1 }));
      }
      setShowExamCompleteConfirm(false);
    }
  };

  // Handle exam exit (back to exam list)
  const exitExam = () => {
    setSelectedExam(null);
    setCurrentExamSection(0);
    setCurrentExamQuestion(0);
    setExamAnswers({});
    setShowExamCompleteConfirm(false);
  };

  // Handle exam answer selection
  const selectExamAnswer = (questionId, answerIndex) => {
    // Create unique key combining exam ID, section ID, and question ID to avoid collisions
    const answerKey = `${selectedExam.id}-${currentExamSection}-${questionId}`;
    // Allow changing answers before submitting
    setExamAnswers(prev => ({ ...prev, [answerKey]: answerIndex }));

    // Exam mode: auto-advance to next question
    if (examMode === 'exam') {
      setTimeout(() => {
        if (!selectedExam) return;

        const currentSection = selectedExam.sections[currentExamSection];
        if (currentExamQuestion < currentSection.questions.length - 1) {
          // Move to next question in current section
          setCurrentExamQuestion(currentExamQuestion + 1);
          setShowExplanation(false);
        } else if (currentExamSection < selectedExam.sections.length - 1) {
          // Move to first question of next section
          setCurrentExamSection(currentExamSection + 1);
          setCurrentExamQuestion(0);
          setShowExplanation(false);
        } else {
          // Last question - show complete dialog
          setShowExamCompleteConfirm(true);
        }
      }, 300); // Small delay for visual feedback
    } else if (examMode === 'study') {
      // Study mode: show explanation
      setShowExplanation(true);
    }
  };

  // Navigate exam questions
  const nextExamQuestion = () => {
    if (!selectedExam) return;

    const currentSection = selectedExam.sections[currentExamSection];
    if (currentExamQuestion < currentSection.questions.length - 1) {
      // Move to next question in current section
      setCurrentExamQuestion(currentExamQuestion + 1);
      setShowExplanation(false);
    } else if (currentExamSection < selectedExam.sections.length - 1) {
      // Move to first question of next section
      setCurrentExamSection(currentExamSection + 1);
      setCurrentExamQuestion(0);
      setShowExplanation(false);
    }
  };

  const prevExamQuestion = () => {
    if (currentExamQuestion > 0) {
      // Move to previous question in current section
      setCurrentExamQuestion(currentExamQuestion - 1);
    } else if (currentExamSection > 0) {
      // Move to last question of previous section
      setCurrentExamSection(currentExamSection - 1);
      const prevSection = selectedExam.sections[currentExamSection - 1];
      setCurrentExamQuestion(prevSection.questions.length - 1);
    }
  };

  // Get total question number for exam progress
  const getCurrentExamQuestionNumber = () => {
    if (!selectedExam) return 0;
    let total = 0;
    for (let i = 0; i < currentExamSection; i++) {
      total += selectedExam.sections[i].questions.length;
    }
    return total + currentExamQuestion + 1;
  };

  const isLastExamQuestion = () => {
    if (!selectedExam) return false;
    return currentExamSection === selectedExam.sections.length - 1 &&
           currentExamQuestion === selectedExam.sections[currentExamSection].questions.length - 1;
  };

  // Check if feature is locked
  const isLocked = (feature) => {
    // Premium users never have locked content
    if (isPremium) {
      return false;
    }

    // Free trial users have limits
    switch (feature) {
      case 'flashcards':
        return trialUsage.flashcardsUsed >= 1; // Flashcards are available once, then locked
      case 'quizzes':
        return trialUsage.quizzesCompleted >= TRIAL_LIMITS.quizzes;
      case 'exams':
        return trialUsage.examsCompleted >= TRIAL_LIMITS.exams;
      default:
        return false;
    }
  };

  // Render Dashboard
  const renderDashboard = () => {
    console.log('Rendering dashboard, isPremium:', isPremium, 'subscriptionLoading:', subscriptionLoading);
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-red-600 text-white p-8 rounded-2xl shadow-xl">
          <h1 className="text-4xl font-black mb-2">Welcome, {user?.name || 'Recruit'}!</h1>
          <p className="text-xl">
            {isPremium ? (
              <span>Ready to ace your ASVAB? Let's get started!</span>
            ) : (
              <span>🎖️ Free Trial - Start Your ASVAB Journey</span>
            )}
          </p>
        </div>

      {/* Career Path Selection/Display */}
      {!selectedCareerPath ? (
        <div className="bg-white p-8 rounded-xl shadow-lg border-4 border-indigo-200">
          <h2 className="text-3xl font-black mb-4 text-center">Choose Your Army Career Path</h2>
          <p className="text-gray-600 text-center mb-6">Select the Army job category you're interested in to see which ASVAB subjects you need to focus on</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {careerPathsData.map((career) => (
              <button
                key={career.id}
                onClick={() => selectCareerPath(career)}
                className="p-6 border-2 border-gray-300 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition text-left group"
              >
                <div className="text-4xl mb-3">{career.icon}</div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-indigo-600">{career.name}</h3>
                <p className="text-sm text-gray-600">Click to learn more</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-xl shadow-lg border-4 border-indigo-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">{selectedCareerPath.icon}</span>
                <h2 className="text-3xl font-black">{selectedCareerPath.name}</h2>
              </div>
              <p className="text-gray-700 text-lg mb-4">{selectedCareerPath.description}</p>

              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Required ASVAB Subjects:</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCareerPath.requiredSubjects.map((subject, idx) => (
                    <span key={idx} className="px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full font-semibold">
                      {subject}
                    </span>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setActiveTab('exams')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xl font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition shadow-lg"
              >
                Start Studying Now
              </button>
            </div>
            <button
              onClick={() => setSelectedCareerPath(null)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold"
            >
              Change Career
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Flashcards Usage */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-4 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <BookOpen className="w-10 h-10 text-blue-600" />
            <span className={`text-2xl font-bold ${isPremium ? 'text-blue-600' : (isLocked('flashcards') ? 'text-red-600' : 'text-blue-600')}`}>
              {isPremium ? 'Unlimited' : (isLocked('flashcards') ? 'Used' : 'Available')}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">Flashcards</h3>
          <p className="text-gray-600 text-sm mb-2">{isPremium ? 'Full access to all ASVAB flashcards' : '15 ASVAB flashcards covering all subjects'}</p>
          {!isPremium && (
            <>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all"
                  style={{ width: `${isLocked('flashcards') ? 100 : 0}%` }}
                ></div>
              </div>
              {isLocked('flashcards') && (
                <p className="text-red-600 text-sm mt-2 font-semibold">⚠️ Already used</p>
              )}
            </>
          )}
        </div>

        {/* Quizzes Usage */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-4 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <Target className="w-10 h-10 text-green-600" />
            <span className={`text-2xl font-bold ${isPremium ? 'text-green-600' : (isLocked('quizzes') ? 'text-red-600' : 'text-green-600')}`}>
              {isPremium ? 'Unlimited' : `${trialUsage.quizzesCompleted}/${TRIAL_LIMITS.quizzes}`}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">Quizzes</h3>
          <p className="text-gray-600 text-sm mb-2">{isPremium ? 'Full access to all practice quizzes' : 'Practice quizzes to test your knowledge'}</p>
          {!isPremium && (
            <>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all"
                  style={{ width: `${(trialUsage.quizzesCompleted / TRIAL_LIMITS.quizzes) * 100}%` }}
                ></div>
              </div>
              {isLocked('quizzes') && (
                <p className="text-red-600 text-sm mt-2 font-semibold">⚠️ Limit reached</p>
              )}
            </>
          )}
        </div>

        {/* Exams Usage */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-4 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <Award className="w-10 h-10 text-purple-600" />
            <span className={`text-2xl font-bold ${isPremium ? 'text-purple-600' : (isLocked('exams') ? 'text-red-600' : 'text-purple-600')}`}>
              {isPremium ? 'Unlimited' : `${trialUsage.examsCompleted}/${TRIAL_LIMITS.exams}`}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">Practice Exams</h3>
          <p className="text-gray-600 text-sm mb-2">{isPremium ? 'Full-length ASVAB practice exams' : 'Take a full practice exam'}</p>
          {!isPremium && (
            <>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-600 h-3 rounded-full transition-all"
                  style={{ width: `${(trialUsage.examsCompleted / TRIAL_LIMITS.exams) * 100}%` }}
                ></div>
              </div>
              {isLocked('exams') && (
                <p className="text-red-600 text-sm mt-2 font-semibold">⚠️ Limit reached</p>
              )}
            </>
          )}
        </div>
      </div>

      {!subscription && (
        <div className="bg-yellow-50 border-4 border-yellow-400 p-6 rounded-xl">
          <h3 className="text-2xl font-bold text-yellow-800 mb-3">🎁 Unlock Full Access</h3>
          <p className="text-yellow-800 mb-4">Upgrade to get unlimited flashcards, quizzes, and practice exams!</p>
          <button
            onClick={() => window.location.href = '/pricing'}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-red-600 text-white font-bold rounded-lg hover:from-blue-700 hover:to-red-700 transition"
          >
            Upgrade to Premium
          </button>
        </div>
      )}
    </div>
    );
  };

  // Render Flashcards
  const renderFlashcards = () => {
    if (isLocked('flashcards')) {
      return (
        <div className="bg-red-50 border-4 border-red-400 p-8 rounded-xl text-center">
          <Lock className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-3xl font-black text-red-800 mb-4">Flashcards Already Used</h2>
          <p className="text-red-700 text-lg mb-6">You've already accessed the free flashcards. Upgrade for unlimited access!</p>
          <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-red-600 text-white font-bold rounded-lg">
            Upgrade for Unlimited Access
          </button>
        </div>
      );
    }

    // Get count by category (needed for filters) - use ALL flashcards for accurate counts
    const flashcardsByCategory = {};
    ASVAB_CATEGORIES.slice(1).forEach(category => {
      flashcardsByCategory[category] = allFlashcards.filter(c => c.subject === category).length;
    });

    // Filter flashcards by selected category
    const filteredFlashcards = selectedFlashcardCategory === 'All'
      ? flashcards
      : flashcards.filter(card => card.subject === selectedFlashcardCategory);

    // Safety check: if no cards in filtered list, show empty message
    if (filteredFlashcards.length === 0) {
      return (
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Filter by Subject</h3>
            <div className="flex flex-wrap gap-3">
              {ASVAB_CATEGORIES.map((category) => {
                return (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedFlashcardCategory(category);
                      setCurrentFlashcard(0);
                      setShowAnswer(false);
                    }}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      selectedFlashcardCategory === category
                        ? 'bg-blue-600 text-white shadow-lg scale-105'
                        : 'bg-white border-2 border-blue-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="bg-yellow-50 border-4 border-yellow-400 p-8 rounded-xl text-center">
            <p className="text-yellow-800 text-lg font-bold">No flashcards available in this category</p>
            <p className="text-yellow-700 mt-2">Try selecting a different subject or "All"</p>
          </div>
        </div>
      );
    }

    // Safety check: reset to 0 if current index is out of bounds
    const safeCurrentFlashcard = currentFlashcard >= filteredFlashcards.length ? 0 : currentFlashcard;
    const card = filteredFlashcards[safeCurrentFlashcard];

    return (
      <div className="max-w-4xl mx-auto">
        {/* Category Filter Buttons */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800">Filter by Subject</h3>
          <div className="flex flex-wrap gap-3">
            {ASVAB_CATEGORIES.map((category) => {
              const count = category === 'All' ? allFlashcards.length : (flashcardsByCategory[category] || 0);
              return (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedFlashcardCategory(category);
                    setCurrentFlashcard(0);
                    setShowAnswer(false);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedFlashcardCategory === category
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : 'bg-white border-2 border-blue-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  {category} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-xl border-4 border-blue-400 mb-6 max-w-2xl mx-auto">
          <div className="flex justify-end items-center mb-6">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 font-bold rounded-full text-sm">
              {card.subject}
            </span>
          </div>

          <div
            className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl min-h-[300px] flex items-center justify-center cursor-pointer hover:shadow-lg transition"
            onClick={() => setShowAnswer(!showAnswer)}
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">{showAnswer ? 'Answer:' : 'Question:'}</h3>
              <p className="text-xl">{showAnswer ? card.answer : card.question}</p>
              {!showAnswer && (
                <p className="text-sm text-gray-600 mt-4">Click to reveal answer</p>
              )}
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={() => {
                setShowAnswer(false);
                if (safeCurrentFlashcard > 0) setCurrentFlashcard(safeCurrentFlashcard - 1);
              }}
              disabled={safeCurrentFlashcard === 0}
              className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-400 transition disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => {
                if (safeCurrentFlashcard < filteredFlashcards.length - 1) {
                  setCurrentFlashcard(safeCurrentFlashcard + 1);
                  setShowAnswer(false);
                  // Track that user has viewed flashcards (mark feature as used on first card)
                  if (safeCurrentFlashcard === 0 && trialUsage.flashcardsUsed === 0) {
                    setTrialUsage(prev => ({ ...prev, flashcardsUsed: 1 }));
                  }
                }
              }}
              disabled={safeCurrentFlashcard >= filteredFlashcards.length - 1}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg hover:from-blue-700 hover:to-blue-800 transition disabled:opacity-50"
            >
              Next Card
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Quizzes
  const renderQuizzes = () => {
    // Show review page if quiz was completed
    if (quizReviewData) {
      const exitReview = () => {
        setQuizReviewData(null);
        setSelectedQuiz(null);
        setCurrentQuizQuestion(0);
        setQuizAnswers({});
        setActiveTab('dashboard');
      };

      return (
        <div className="max-w-5xl mx-auto">
          {/* Score Summary */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl shadow-xl border-4 border-blue-400 mb-6">
            <h2 className="text-4xl font-black mb-4 text-gray-800">Quiz Complete!</h2>
            <h3 className="text-2xl font-bold mb-6 text-gray-700">{quizReviewData.quiz.title}</h3>
            <div className="grid grid-cols-3 gap-6 mb-4">
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-5xl font-black text-blue-600 mb-2">{quizReviewData.percentage}%</p>
                <p className="text-gray-600 font-semibold">Overall Score</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-5xl font-black text-green-600 mb-2">{quizReviewData.score}</p>
                <p className="text-gray-600 font-semibold">Correct Answers</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-5xl font-black text-red-600 mb-2">{quizReviewData.total - quizReviewData.score}</p>
                <p className="text-gray-600 font-semibold">Incorrect Answers</p>
              </div>
            </div>
            <p className="text-center text-gray-700 font-semibold">Review your answers below</p>
          </div>

          {/* Question Review */}
          <div className="space-y-6">
            {quizReviewData.quiz.questions.map((question, qIndex) => {
              const answerKey = `${quizReviewData.quiz.id}-${question.id}`;
              const userAnswer = quizReviewData.answers[answerKey];
              const isCorrect = userAnswer === question.correctAnswer;

              return (
                <div key={question.id} className={`bg-white p-6 rounded-xl shadow-lg border-4 ${
                  isCorrect ? 'border-green-400' : 'border-red-400'
                }`}>
                  {/* Question Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Question {qIndex + 1}</h3>
                    <span className={`px-4 py-2 rounded-full font-bold text-white ${
                      isCorrect ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                      {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  </div>

                  {/* Passage if exists */}
                  {question.passage && (
                    <div className="bg-blue-50 p-4 rounded-lg mb-4 border-2 border-blue-200">
                      <p className="text-sm font-semibold text-blue-700 mb-2">Reading Passage:</p>
                      <p className="text-gray-800 leading-relaxed">{question.passage}</p>
                    </div>
                  )}

                  {/* Question Text */}
                  <p className="text-lg font-semibold text-gray-800 mb-4">{question.question}</p>

                  {/* Options */}
                  <div className="space-y-3 mb-4">
                    {question.options.map((option, optIndex) => {
                      const isUserAnswer = userAnswer === optIndex;
                      const isCorrectAnswer = question.correctAnswer === optIndex;

                      let optionClasses = 'p-4 rounded-lg border-2 ';
                      if (isCorrectAnswer) {
                        optionClasses += 'bg-green-100 border-green-600';
                      } else if (isUserAnswer && !isCorrect) {
                        optionClasses += 'bg-red-100 border-red-600';
                      } else {
                        optionClasses += 'bg-gray-50 border-gray-300';
                      }

                      return (
                        <div key={optIndex} className={optionClasses}>
                          <div className="flex items-center gap-3">
                            {isCorrectAnswer && (
                              <span className="text-green-700 font-bold">✓</span>
                            )}
                            {isUserAnswer && !isCorrect && (
                              <span className="text-red-700 font-bold">✗</span>
                            )}
                            <span className={`font-semibold ${
                              isCorrectAnswer ? 'text-green-900' :
                              isUserAnswer && !isCorrect ? 'text-red-900' : 'text-gray-700'
                            }`}>
                              {option}
                            </span>
                            {isUserAnswer && (
                              <span className={`ml-auto text-sm font-bold ${
                                isCorrect ? 'text-green-700' : 'text-red-700'
                              }`}>
                                Your Answer
                              </span>
                            )}
                            {isCorrectAnswer && !isUserAnswer && (
                              <span className="ml-auto text-sm font-bold text-green-700">
                                Correct Answer
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Explanation */}
                  <div className="bg-blue-50 border-2 border-blue-300 p-4 rounded-lg">
                    <p className="font-bold text-blue-900 mb-1">Explanation:</p>
                    <p className="text-blue-800">{question.explanation}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Exit Button */}
          <div className="mt-8 text-center">
            <button
              onClick={exitReview}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold text-xl rounded-xl hover:from-blue-700 hover:to-blue-800 transition shadow-lg"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    if (selectedQuiz) {
      const currentQuestion = selectedQuiz.questions[currentQuizQuestion];
      const isLastQuestion = currentQuizQuestion === selectedQuiz.questions.length - 1;
      // Use composite key to match the storage key used in selectQuizAnswer
      const answerKey = `${selectedQuiz.id}-${currentQuestion.id}`;
      const selectedAnswer = quizAnswers[answerKey];

      return (
        <div className="max-w-3xl mx-auto">
          <div className="bg-white p-8 rounded-xl shadow-xl border-4 border-green-400">
            <h2 className="text-3xl font-black mb-4">{selectedQuiz.title}</h2>
            <p className="text-gray-600 mb-6">{selectedQuiz.description}</p>

            {/* Progress bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Question {currentQuizQuestion + 1} of {selectedQuiz.questions.length}</span>
                <span>{Math.round(((currentQuizQuestion + 1) / selectedQuiz.questions.length) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${((currentQuizQuestion + 1) / selectedQuiz.questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-green-50 p-6 rounded-lg mb-6">
              {/* Display passage if it exists */}
              {currentQuestion.passage && (
                <div className="bg-white p-4 rounded-lg mb-4 border-2 border-gray-300">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Reading Passage:</p>
                  <p className="text-gray-800 leading-relaxed">{currentQuestion.passage}</p>
                </div>
              )}
              <p className="text-xl font-bold mb-4">{currentQuestion.question}</p>
              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectQuizAnswer(currentQuestion.id, idx)}
                    className={`w-full p-4 text-left border-2 rounded-lg transition font-semibold ${
                      selectedAnswer === idx
                        ? 'bg-green-100 border-green-500 ring-2 ring-green-300'
                        : 'bg-white border-gray-300 hover:border-green-500 hover:bg-green-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedAnswer === idx ? 'border-green-600 bg-green-600' : 'border-gray-400'
                      }`}>
                        {selectedAnswer === idx && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <span>{option}</span>
                    </div>
                  </button>
                ))}
              </div>

              {showExplanation && quizMode === 'study' && (
                <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <p className="text-sm font-bold text-blue-800 mb-1">Explanation:</p>
                  <p className="text-sm text-blue-900">{currentQuestion.explanation}</p>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={exitQuiz}
                className="px-6 py-3 bg-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-400 transition"
              >
                Back
              </button>
              <button
                onClick={prevQuizQuestion}
                disabled={currentQuizQuestion === 0}
                className="px-6 py-3 bg-gray-500 text-white font-bold rounded-lg hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {!isLastQuestion ? (
                <button
                  onClick={nextQuizQuestion}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg hover:from-green-700 hover:to-green-800 transition"
                >
                  Next Question
                </button>
              ) : (
                <button
                  onClick={() => setShowQuizCompleteConfirm(true)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg hover:from-green-700 hover:to-green-800 transition"
                >
                  Complete Quiz
                </button>
              )}
            </div>

            {/* Confirmation Modal */}
            {showQuizCompleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md">
                  <h3 className="text-2xl font-bold mb-4">Complete Quiz?</h3>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to complete this quiz? This will use one of your free quiz attempts.
                  </p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowQuizCompleteConfirm(false)}
                      className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-400 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={completeQuiz}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg hover:from-green-700 hover:to-green-800 transition"
                    >
                      Yes, Complete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Filter quizzes by selected category
    const filteredQuizzes = selectedQuizCategory === 'All'
      ? quizzes
      : quizzes.filter(quiz => quiz.category === selectedQuizCategory);

    // Get count by category
    const quizzesByCategory = {};
    ASVAB_CATEGORIES.slice(1).forEach(category => {
      quizzesByCategory[category] = quizzes.filter(q => q.category === category).length;
    });

    // Separate filtered quizzes into selected and other categories
    const otherQuizzes = selectedQuizCategory !== 'All'
      ? quizzes.filter(quiz => quiz.category !== selectedQuizCategory)
      : [];

    return (
      <div className="max-w-4xl mx-auto">
        {isLocked('quizzes') && (
          <div className="bg-red-50 border-4 border-red-400 p-6 rounded-xl mb-6 text-center">
            <Lock className="w-12 h-12 text-red-600 mx-auto mb-3" />
            <p className="text-red-700 font-bold">You've completed all {TRIAL_LIMITS.quizzes} free quizzes. Upgrade for unlimited access!</p>
          </div>
        )}

        {/* Category Filter Buttons */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800">Filter by Subject</h3>
          <div className="flex flex-wrap gap-3">
            {ASVAB_CATEGORIES.map((category) => {
              return (
                <button
                  key={category}
                  onClick={() => setSelectedQuizCategory(category)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedQuizCategory === category
                      ? 'bg-green-600 text-white shadow-lg scale-105'
                      : 'bg-white border-2 border-green-200 text-gray-700 hover:border-green-400 hover:bg-green-50'
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Category Quizzes */}
        {selectedQuizCategory !== 'All' && filteredQuizzes.length > 0 && (
          <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-4">
              {selectedQuizCategory} Quizzes
            </h4>
          </div>
        )}

        <div className="grid gap-6">
          {filteredQuizzes.map((quiz) => {
            const isQuizLocked = quiz.id !== 1 && isLocked('quizzes');
            const isFreeTrialQuiz = quiz.id === 1;

            return (
              <div key={quiz.id} className={`bg-gradient-to-br p-8 rounded-xl shadow-xl border-4 transition-all hover:shadow-2xl ${
                isQuizLocked && !isFreeTrialQuiz
                  ? 'from-gray-50 to-gray-100 border-gray-300 opacity-75'
                  : 'from-white to-green-50 border-green-300 hover:border-green-400'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className={`text-3xl font-black ${isQuizLocked && !isFreeTrialQuiz ? 'text-gray-600' : 'text-gray-800'}`}>
                        {quiz.title}
                      </h3>
                      {isFreeTrialQuiz && (
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                          FREE TRIAL
                        </span>
                      )}
                      {isQuizLocked && !isFreeTrialQuiz && (
                        <span className="px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
                          🔒 PREMIUM
                        </span>
                      )}
                    </div>
                    <p className={`mb-4 text-lg ${isQuizLocked && !isFreeTrialQuiz ? 'text-gray-600' : 'text-gray-700'}`}>
                      {quiz.description}
                    </p>
                    <div className={`flex gap-4 font-semibold ${isQuizLocked && !isFreeTrialQuiz ? 'text-gray-500' : 'text-gray-600'}`}>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">📝</span>
                        {quiz.questions.length} questions
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">⏱️</span>
                        {quiz.timeLimit} min
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => startQuiz(quiz)}
                    disabled={isQuizLocked}
                    className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-xl hover:from-green-700 hover:to-green-800 transition disabled:opacity-50 disabled:cursor-not-allowed text-xl flex items-center gap-2 shadow-lg"
                  >
                    {isQuizLocked ? (
                      <>
                        <Lock className="w-6 h-6" />
                        {quiz.id === 1 ? 'Completed' : 'Upgrade'}
                      </>
                    ) : (
                      <>
                        <Target className="w-6 h-6" />
                        Start Quiz
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Other Categories Section */}
        {selectedQuizCategory !== 'All' && otherQuizzes.length > 0 && (
          <div className="mt-12">
            <h4 className="text-xl font-bold text-gray-800 mb-4">
              Other Categories
            </h4>
            <div className="grid gap-6">
              {otherQuizzes.map((quiz) => {
                const isQuizLocked = quiz.id !== 1 && isLocked('quizzes');
                const isFreeTrialQuiz = quiz.id === 1;

                return (
                  <div key={quiz.id} className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-xl shadow-lg border-2 border-gray-200 hover:border-gray-300 transition-all hover:shadow-xl opacity-90">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-2xl font-bold text-gray-700">{quiz.title}</h3>
                          <span className="px-3 py-1 bg-gray-500 text-white text-xs font-bold rounded-full">
                            {quiz.category}
                          </span>
                          {isQuizLocked && !isFreeTrialQuiz && (
                            <span className="px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
                              🔒 PREMIUM
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 mb-4 text-base">{quiz.description}</p>
                        <div className="flex gap-4 text-gray-500 font-semibold">
                          <span className="flex items-center gap-2">
                            <span className="text-lg">📝</span>
                            {quiz.questions.length} questions
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-lg">⏱️</span>
                            {quiz.timeLimit} min
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => startQuiz(quiz)}
                        disabled={isQuizLocked}
                        className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl hover:from-gray-600 hover:to-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2 shadow-lg"
                      >
                        {isQuizLocked ? (
                          <>
                            <Lock className="w-5 h-5" />
                            {quiz.id === 1 ? 'Completed' : 'Upgrade'}
                          </>
                        ) : (
                          <>
                            <Target className="w-5 h-5" />
                            Start Quiz
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Exams
  const renderExams = () => {
    // Show review page if exam was completed
    if (examReviewData) {
      const exitReview = () => {
        setExamReviewData(null);
        setSelectedExam(null);
        setCurrentExamSection(0);
        setCurrentExamQuestion(0);
        setExamAnswers({});
        setActiveTab('dashboard');
      };

      // Calculate question number across sections
      let questionCounter = 0;

      return (
        <div className="max-w-5xl mx-auto">
          {/* Score Summary */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-xl shadow-xl border-4 border-purple-400 mb-6">
            <h2 className="text-4xl font-black mb-4 text-gray-800">Exam Complete!</h2>
            <h3 className="text-2xl font-bold mb-6 text-gray-700">{examReviewData.exam.title}</h3>
            <div className="grid grid-cols-3 gap-6 mb-4">
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-5xl font-black text-purple-600 mb-2">{examReviewData.percentage}%</p>
                <p className="text-gray-600 font-semibold">Overall Score</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-5xl font-black text-green-600 mb-2">{examReviewData.score}</p>
                <p className="text-gray-600 font-semibold">Correct Answers</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center shadow-md">
                <p className="text-5xl font-black text-red-600 mb-2">{examReviewData.total - examReviewData.score}</p>
                <p className="text-gray-600 font-semibold">Incorrect Answers</p>
              </div>
            </div>
            <p className="text-center text-gray-700 font-semibold">Review your answers below organized by section</p>
          </div>

          {/* Section Reviews */}
          <div className="space-y-8">
            {examReviewData.exam.sections.map((section, sectionIndex) => {
              // Calculate section score
              let sectionCorrect = 0;
              section.questions.forEach(question => {
                const answerKey = `${examReviewData.exam.id}-${sectionIndex}-${question.id}`;
                if (examReviewData.answers[answerKey] === question.correctAnswer) {
                  sectionCorrect++;
                }
              });
              const sectionPercentage = Math.round((sectionCorrect / section.questions.length) * 100);

              return (
                <div key={section.id} className="bg-white p-6 rounded-xl shadow-xl border-4 border-purple-300">
                  {/* Section Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 rounded-lg mb-6">
                    <h3 className="text-2xl font-black text-white mb-2">{section.name}</h3>
                    <div className="flex items-center gap-4 text-white">
                      <span className="font-bold">Score: {sectionPercentage}%</span>
                      <span className="font-semibold">({sectionCorrect}/{section.questions.length} correct)</span>
                    </div>
                  </div>

                  {/* Section Questions */}
                  <div className="space-y-6">
                    {section.questions.map((question) => {
                      questionCounter++;
                      const answerKey = `${examReviewData.exam.id}-${sectionIndex}-${question.id}`;
                      const userAnswer = examReviewData.answers[answerKey];
                      const isCorrect = userAnswer === question.correctAnswer;

                      return (
                        <div key={question.id} className={`p-6 rounded-lg border-4 ${
                          isCorrect ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'
                        }`}>
                          {/* Question Header */}
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-bold text-gray-800">Question {questionCounter}</h4>
                            <span className={`px-4 py-2 rounded-full font-bold text-white ${
                              isCorrect ? 'bg-green-600' : 'bg-red-600'
                            }`}>
                              {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                            </span>
                          </div>

                          {/* Passage if exists */}
                          {question.passage && (
                            <div className="bg-blue-50 p-4 rounded-lg mb-4 border-2 border-blue-200">
                              <p className="text-sm font-semibold text-blue-700 mb-2">Reading Passage:</p>
                              <p className="text-gray-800 leading-relaxed">{question.passage}</p>
                            </div>
                          )}

                          {/* Question Text */}
                          <p className="text-lg font-semibold text-gray-800 mb-4">{question.question}</p>

                          {/* Options */}
                          <div className="space-y-3 mb-4">
                            {question.options.map((option, optIndex) => {
                              const isUserAnswer = userAnswer === optIndex;
                              const isCorrectAnswer = question.correctAnswer === optIndex;

                              let optionClasses = 'p-4 rounded-lg border-2 ';
                              if (isCorrectAnswer) {
                                optionClasses += 'bg-green-100 border-green-600';
                              } else if (isUserAnswer && !isCorrect) {
                                optionClasses += 'bg-red-100 border-red-600';
                              } else {
                                optionClasses += 'bg-white border-gray-300';
                              }

                              return (
                                <div key={optIndex} className={optionClasses}>
                                  <div className="flex items-center gap-3">
                                    {isCorrectAnswer && (
                                      <span className="text-green-700 font-bold">✓</span>
                                    )}
                                    {isUserAnswer && !isCorrect && (
                                      <span className="text-red-700 font-bold">✗</span>
                                    )}
                                    <span className={`font-semibold ${
                                      isCorrectAnswer ? 'text-green-900' :
                                      isUserAnswer && !isCorrect ? 'text-red-900' : 'text-gray-700'
                                    }`}>
                                      {option}
                                    </span>
                                    {isUserAnswer && (
                                      <span className={`ml-auto text-sm font-bold ${
                                        isCorrect ? 'text-green-700' : 'text-red-700'
                                      }`}>
                                        Your Answer
                                      </span>
                                    )}
                                    {isCorrectAnswer && !isUserAnswer && (
                                      <span className="ml-auto text-sm font-bold text-green-700">
                                        Correct Answer
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Explanation */}
                          <div className="bg-blue-50 border-2 border-blue-300 p-4 rounded-lg">
                            <p className="font-bold text-blue-900 mb-1">Explanation:</p>
                            <p className="text-blue-800">{question.explanation}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Exit Button */}
          <div className="mt-8 text-center">
            <button
              onClick={exitReview}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold text-xl rounded-xl hover:from-purple-700 hover:to-purple-800 transition shadow-lg"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    if (selectedExam) {
      const currentSection = selectedExam.sections[currentExamSection];
      const currentQuestion = currentSection.questions[currentExamQuestion];
      const questionNumber = getCurrentExamQuestionNumber();
      // Use composite key to match the storage key used in selectExamAnswer
      const answerKey = `${selectedExam.id}-${currentExamSection}-${currentQuestion.id}`;
      const selectedAnswer = examAnswers[answerKey];
      const isLast = isLastExamQuestion();

      return (
        <div className="max-w-3xl mx-auto">
          <div className="bg-white p-8 rounded-xl shadow-xl border-4 border-purple-400">
            <h2 className="text-3xl font-black mb-4">{selectedExam.title}</h2>
            <p className="text-gray-600 mb-6">{selectedExam.description}</p>

            {/* Section indicator */}
            <div className="mb-4 p-3 bg-purple-100 rounded-lg">
              <p className="font-bold text-purple-800">Section {currentExamSection + 1} of {selectedExam.sections.length}: {currentSection.name}</p>
              <p className="text-sm text-purple-600">Question {currentExamQuestion + 1} of {currentSection.questions.length} in this section</p>
            </div>

            {/* Progress bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Overall Question {questionNumber} of {selectedExam.totalQuestions}</span>
                <span>{Math.round((questionNumber / selectedExam.totalQuestions) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${(questionNumber / selectedExam.totalQuestions) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              {/* Display passage if it exists */}
              {currentQuestion.passage && (
                <div className="bg-white p-4 rounded-lg mb-4 border-2 border-gray-300">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Reading Passage:</p>
                  <p className="text-gray-800 leading-relaxed">{currentQuestion.passage}</p>
                </div>
              )}
              <p className="text-xl font-bold mb-4">{currentQuestion.question}</p>
              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectExamAnswer(currentQuestion.id, idx)}
                    className={`w-full p-4 text-left border-2 rounded-lg transition font-semibold ${
                      selectedAnswer === idx
                        ? 'bg-purple-100 border-purple-500 ring-2 ring-purple-300'
                        : 'bg-white border-gray-300 hover:border-purple-500 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedAnswer === idx ? 'border-purple-600 bg-purple-600' : 'border-gray-400'
                      }`}>
                        {selectedAnswer === idx && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <span>{option}</span>
                    </div>
                  </button>
                ))}
              </div>

              {showExplanation && examMode === 'study' && (
                <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <p className="text-sm font-bold text-blue-800 mb-1">Explanation:</p>
                  <p className="text-sm text-blue-900">{currentQuestion.explanation}</p>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={exitExam}
                className="px-6 py-3 bg-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-400 transition"
              >
                Back
              </button>
              <button
                onClick={prevExamQuestion}
                disabled={currentExamSection === 0 && currentExamQuestion === 0}
                className="px-6 py-3 bg-gray-500 text-white font-bold rounded-lg hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {!isLast ? (
                <button
                  onClick={nextExamQuestion}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-lg hover:from-purple-700 hover:to-purple-800 transition"
                >
                  Next Question
                </button>
              ) : (
                <button
                  onClick={() => setShowExamCompleteConfirm(true)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-lg hover:from-purple-700 hover:to-purple-800 transition"
                >
                  Complete Exam
                </button>
              )}
            </div>

            {/* Confirmation Modal */}
            {showExamCompleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md">
                  <h3 className="text-2xl font-bold mb-4">Complete Exam?</h3>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to complete this exam? This will use your free practice exam attempt.
                  </p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowExamCompleteConfirm(false)}
                      className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-400 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={completeExam}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-lg hover:from-purple-700 hover:to-purple-800 transition"
                    >
                      Yes, Complete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto">
        {isLocked('exams') && (
          <div className="bg-red-50 border-4 border-red-400 p-6 rounded-xl mb-6 text-center">
            <Lock className="w-12 h-12 text-red-600 mx-auto mb-3" />
            <p className="text-red-700 font-bold">You've completed your free practice exam. Upgrade for unlimited access!</p>
          </div>
        )}

        <div className="grid gap-6">
          {exams.map((exam) => {
            const isExamLocked = exam.id !== 1 && isLocked('exams');
            const isFreeTrialExam = exam.id === 1;
            const examCompleted = examHistory[exam.id];

            return (
              <div key={exam.id} className={`bg-gradient-to-br p-8 rounded-xl shadow-xl border-4 transition-all hover:shadow-2xl ${
                isExamLocked && !isFreeTrialExam
                  ? 'from-gray-50 to-gray-100 border-gray-300 opacity-75'
                  : examCompleted
                  ? 'from-white to-green-50 border-green-300 hover:border-green-400'
                  : 'from-white to-purple-50 border-purple-300 hover:border-purple-400'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className={`text-3xl font-black ${isExamLocked && !isFreeTrialExam ? 'text-gray-600' : 'text-gray-800'}`}>
                        {exam.title}
                      </h3>
                      <span className={`px-3 py-1 text-white text-xs font-bold rounded-full ${
                        isFreeTrialExam ? 'bg-purple-600' : 'bg-gray-500'
                      }`}>
                        {isFreeTrialExam ? 'FREE TRIAL' : 'PRACTICE TEST'}
                      </span>
                      {examCompleted && (
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                          ✓ COMPLETED {examCompleted.percentage}%
                        </span>
                      )}
                      {isExamLocked && !isFreeTrialExam && (
                        <span className="px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
                          🔒 PREMIUM
                        </span>
                      )}
                    </div>
                    <p className={`mb-4 text-lg ${isExamLocked && !isFreeTrialExam ? 'text-gray-600' : 'text-gray-700'}`}>
                      {exam.description}
                    </p>
                    <div className={`flex gap-4 font-semibold ${isExamLocked && !isFreeTrialExam ? 'text-gray-500' : 'text-gray-600'}`}>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">📝</span>
                        {exam.totalQuestions} questions
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">⏱️</span>
                        {exam.timeLimit} min
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">📚</span>
                        {exam.sections.length} sections
                      </span>
                    </div>
                  </div>
                  {examCompleted ? (
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => setExamReviewData(examCompleted)}
                        className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-xl hover:from-green-700 hover:to-green-800 transition text-lg flex items-center justify-center gap-2 shadow-lg"
                      >
                        <Award className="w-5 h-5" />
                        Review Results
                      </button>
                      <button
                        onClick={() => startExam(exam)}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-xl hover:from-purple-700 hover:to-purple-800 transition text-lg flex items-center justify-center gap-2 shadow-lg"
                      >
                        <Award className="w-5 h-5" />
                        Retake Exam
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startExam(exam)}
                      disabled={isExamLocked}
                      className="px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-xl hover:from-purple-700 hover:to-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed text-xl flex items-center gap-2 shadow-lg"
                    >
                      {isExamLocked ? (
                        <>
                          <Lock className="w-6 h-6" />
                          Upgrade
                        </>
                      ) : (
                        <>
                          <Award className="w-6 h-6" />
                          Start Exam
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Membership Page
  const renderMembership = () => {
    // Subscription plans
    const plans = [
      {
        id: 'monthly',
        name: 'Monthly',
        price: 29,
        interval: 'month',
        features: [
          'Unlimited ASVAB flashcards',
          'Unlimited practice quizzes',
          'Unlimited full-length practice exams',
          'Detailed performance analytics',
          'Career path guidance',
          'Mobile access',
          'Cancel anytime'
        ]
      },
      {
        id: 'quarterly',
        name: '3-Month Plan',
        price: 69,
        interval: '3 months',
        totalMonths: 3,
        savings: 18,
        savingsPercent: 21,
        features: [
          'Everything in Monthly plan',
          '💰 Save $18 (21% off)',
          'Only $23/month',
          'Extended study time for optimal results',
          'Perfect for focused ASVAB prep',
          'Cancel anytime'
        ],
        popular: true
      },
      {
        id: 'biannual',
        name: '6-Month Plan',
        price: 119,
        interval: '6 months',
        totalMonths: 6,
        savings: 55,
        savingsPercent: 32,
        features: [
          'Everything in Monthly plan',
          '💰 Save $55 (32% off)',
          'Only $19.83/month',
          'Best value for comprehensive prep',
          'Priority support',
          'Downloadable study guides',
          'Advanced score predictions',
          'Cancel anytime'
        ],
        bestValue: true
      }
    ];

    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">Membership & Billing</h2>
          <p className="text-gray-600">Manage your subscription and payment methods</p>
        </div>

        {/* Current Subscription Status */}
        <div className="bg-white rounded-xl shadow-md p-6 border-2 border-blue-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Current Plan</h3>
              <div className="flex items-center gap-2">
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${isPremium ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {isPremium ? 'Premium Member' : 'Free Trial'}
                </div>
              </div>
            </div>
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>

          {isPremium ? (
            <div className="space-y-2 mb-4">
              <p className="text-gray-700">
                <span className="font-semibold">Status:</span> Active Premium
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Premium includes:</span> Unlimited flashcards • Unlimited quizzes • Unlimited practice exams
              </p>
              {subscription && subscription.current_period_end && (
                <p className="text-gray-700">
                  <span className="font-semibold">Renews on:</span> {new Date(subscription.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-4">
                <p className="text-gray-700">
                  <span className="font-semibold">Status:</span> Active Trial
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold">Trial includes:</span> 15 flashcards • 2 quizzes • 1 practice exam
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <span className="font-bold">Ready to unlock full access?</span> Choose a plan below to get unlimited access to all ASVAB study materials.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Available Plans */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Choose Your Plan</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-xl shadow-lg p-6 border-2 transition-all ${
                  plan.popular || plan.bestValue
                    ? 'border-blue-500 ring-4 ring-blue-100'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {plan.popular && (
                  <div className="mb-3">
                    <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full text-xs font-bold uppercase">
                      Most Popular
                    </span>
                  </div>
                )}
                {plan.bestValue && (
                  <div className="mb-3">
                    <span className="px-3 py-1 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-full text-xs font-bold uppercase">
                      🏆 Best Value
                    </span>
                  </div>
                )}

                <h4 className="text-2xl font-black text-gray-900 mb-2">{plan.name}</h4>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-gray-900">${plan.price}</span>
                    <span className="text-gray-600 text-sm">/{plan.interval}</span>
                  </div>
                  {plan.savings && (
                    <div className="mt-2 space-y-1">
                      <p className="text-green-600 font-bold text-lg">Save ${plan.savings}</p>
                      <p className="text-gray-500 text-sm">(${(plan.price / plan.totalMonths).toFixed(2)}/month)</p>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Trophy className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {isPremium && subscription && subscription.plan_name === plan.id ? (
                  <button
                    className="w-full py-3 rounded-lg font-bold bg-green-600 text-white cursor-default"
                    disabled
                  >
                    ✓ Current Plan
                  </button>
                ) : (
                  <button
                    className={`w-full py-3 rounded-lg font-bold transition ${
                      plan.bestValue
                        ? 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-700 hover:to-green-600 shadow-lg'
                        : plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                    onClick={async () => {
                      // If user is premium and trying to change plans
                      if (isPremium) {
                        const confirmChange = window.confirm(
                          `Change to ${plan.name}?\n\n` +
                          `Your plan will change on your next renewal date (${subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'next billing cycle'}).\n\n` +
                          `You won't be charged until then, and you'll keep your current access until the renewal date.\n\n` +
                          `Click OK to proceed with the plan change.`
                        );

                        if (!confirmChange) {
                          return;
                        }

                        // Call backend API to schedule plan change
                        try {
                          const response = await fetch('/api/change-plan', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              auth0_id: user.sub,
                              new_plan_id: plan.id,
                            }),
                          });

                          const data = await response.json();

                          if (response.ok) {
                            alert(`✅ Plan change scheduled!\n\nYour plan will change to ${plan.name} on ${new Date(data.renewal_date).toLocaleDateString()}.\n\nYou'll keep your current access until then.`);
                            // Refresh the page to update the subscription display
                            window.location.reload();
                          } else {
                            alert(`Error: ${data.message || 'Failed to change plan'}\n\nPlease contact support.`);
                          }
                        } catch (error) {
                          alert(`Error: ${error.message}\n\nPlease try again or contact support.`);
                        }
                        return;
                      }

                      // For non-premium users, proceed with normal checkout
                      if (!isStripeConfigured()) {
                        alert('Stripe is not yet configured. Please add your Stripe API keys to the .env file.');
                        return;
                      }

                      try {
                        await redirectToCheckout(plan.id, user?.email || '');
                      } catch (error) {
                        alert(`Error: ${error.message}\n\nPlease try again or contact support.`);
                      }
                    }}
                  >
                    {isPremium ? 'Change to ' : (plan.bestValue ? '🏆 ' : 'Choose ')}{plan.name}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Payment & Billing Management */}
        {subscription && !subscription.stripe_customer_id.startsWith('cus_test') && (
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Billing & Payment Management</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <CreditCard className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-1">Manage Your Subscription</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Update your payment method, view invoices, and manage your subscription through Stripe's secure customer portal.
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/create-portal-session', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ auth0Id: user.sub })
                        });
                        const data = await response.json();
                        if (data.url) {
                          window.location.href = data.url;
                        } else {
                          alert('Unable to open billing portal. Please contact support.');
                        }
                      } catch (error) {
                        console.error('Error opening portal:', error);
                        alert('Unable to open billing portal. Please contact support.');
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                  >
                    <CreditCard className="w-4 h-4" />
                    Open Billing Portal
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500 space-y-1">
                <p>• Update payment method and billing details</p>
                <p>• Download invoices and receipts</p>
                <p>• View payment history</p>
                <p>• Manage subscription settings</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Mode selection modal
  const renderModeSelection = () => {
    if (!showModeSelection) return null;

    const isQuiz = showModeSelection.type === 'quiz';
    const data = showModeSelection.data;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8">
          <h2 className="text-3xl font-black text-gray-900 mb-4">
            Choose Your Mode
          </h2>
          <p className="text-gray-600 mb-6">
            Select how you want to take this {isQuiz ? 'quiz' : 'exam'}
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Exam Mode */}
            <button
              onClick={() => {
                if (isQuiz) {
                  startQuizWithMode(data, 'exam');
                } else {
                  startExamWithMode(data, 'exam');
                }
              }}
              className="group border-2 border-blue-300 rounded-xl p-6 hover:border-blue-500 hover:bg-blue-50 transition text-left"
            >
              <div className="flex items-center gap-3 mb-3">
                <Target className="w-8 h-8 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Exam Mode</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Simulate real test conditions. Answer questions and automatically move to the next.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>✓ Auto-advance after selection</li>
                <li>✓ No explanations shown</li>
                <li>✓ Review answers at the end</li>
                <li>✓ Timed experience</li>
              </ul>
            </button>

            {/* Study Mode */}
            <button
              onClick={() => {
                if (isQuiz) {
                  startQuizWithMode(data, 'study');
                } else {
                  startExamWithMode(data, 'study');
                }
              }}
              className="group border-2 border-green-300 rounded-xl p-6 hover:border-green-500 hover:bg-green-50 transition text-left"
            >
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="w-8 h-8 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900">Study Mode</h3>
              </div>
              <p className="text-gray-700 mb-3">
                Learn as you go. See explanations immediately after each answer.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>✓ Immediate feedback</li>
                <li>✓ Detailed explanations shown</li>
                <li>✓ Can't change answers</li>
                <li>✓ Perfect for learning</li>
              </ul>
            </button>
          </div>

          <button
            onClick={() => setShowModeSelection(null)}
            className="w-full py-3 border-2 border-gray-300 rounded-lg font-bold hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'flashcards':
        return renderFlashcards();
      case 'quizzes':
        return renderQuizzes();
      case 'exams':
        return renderExams();
      case 'membership':
        return renderMembership();
      default:
        return renderDashboard();
    }
  };

  return (
    <>
      {/* Mode Selection Modal */}
      {renderModeSelection()}

      <div className="min-h-screen bg-gray-100 flex">
      {/* Left Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-blue-900 to-red-900 text-white transition-all duration-300 flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-4 flex items-center justify-between border-b border-white/20">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Star className="w-8 h-8 fill-white" />
              <span className="font-black text-xl">StartPatriot</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Navigation Tabs */}
        <nav className="flex-1 p-4 space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-900 font-bold'
                    : 'hover:bg-white/10'
                }`}
              >
                <Icon className="w-6 h-6" />
                {sidebarOpen && <span>{tab.name}</span>}
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/20">
          {sidebarOpen && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-white/60">Logged in as</p>
                {isPremium && (
                  <span className="px-2 py-0.5 bg-yellow-500 text-yellow-900 text-xs font-black rounded">
                    PREMIUM
                  </span>
                )}
              </div>
              <p className="font-bold truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={() => logout({ logoutParams: { returnTo: 'https://www.startpatriot.com' } })}
            className="w-full p-3 bg-red-600 hover:bg-red-700 rounded-lg font-bold transition flex items-center justify-center gap-2"
          >
            <Award className="w-5 h-5" />
            {sidebarOpen && 'Logout'}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Trial/Premium Banner */}
          {!subscriptionLoading && (
            isPremium ? (
              <div className="bg-gradient-to-r from-green-400 to-blue-400 text-white p-4 rounded-xl mb-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-8 h-8" />
                    <div>
                      <p className="font-black text-lg">🌟 Premium Member Active</p>
                      <p className="text-sm">Unlimited access to all content</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 p-4 rounded-xl mb-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-8 h-8" />
                    <div>
                      <p className="font-black text-lg">🎯 Free Trial Active</p>
                      <p className="text-sm">15 flashcards • 2 quizzes • 1 practice exam</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('membership')}
                    className="px-6 py-2 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 transition"
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            )
          )}

          {/* Content */}
          {renderContent()}
        </div>
      </div>
    </div>
    </>
  );
};

export default FreeTrialApp;
